import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { Menu, X, Phone, MapPin, ChevronDown } from "lucide-react";
import { HOSPITAL, SERVICES, DOCTORS } from "../lib/hospitalData";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services", mega: "services" },
  { to: "/doctors", label: "Doctors", mega: "doctors" },
  { to: "/facilities", label: "Facilities" },
  { to: "/contact", label: "Contact" },
];

const MEGA_CONFIG = {
  services: {
    eyebrow: "Our Specialties",
    title: "Super-Specialty Care, All Under One Roof",
    desc: "Hover any service to learn more, or visit the full Services page.",
    ctaLabel: "View All Services",
    ctaTo: "/services",
    items: SERVICES.map((s) => ({ id: s.id, name: s.name, to: `/services#${s.id}` })),
  },
  doctors: {
    eyebrow: "Our Directors & Doctors",
    title: "Hands you can trust with your heart",
    desc: "Meet our super-specialists leading cardiac, ENT and oncology care.",
    ctaLabel: "View All Doctors",
    ctaTo: "/doctors",
    items: DOCTORS.map((d) => ({ id: d.id, name: d.name, sub: d.title, to: `/doctors#${d.id}` })),
  },
};

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeMega, setActiveMega] = useState(null); // 'services' | 'doctors' | null
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const closeTimer = useRef(null);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); setActiveMega(null); setMobileExpanded(null); }, [loc.pathname]);

  const showMega = (key) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setActiveMega(key);
  };
  const hideMega = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setActiveMega(null), 120);
  };

  const cfg = activeMega ? MEGA_CONFIG[activeMega] : null;
  const items = cfg?.items || [];
  const half = Math.ceil(items.length / 2);
  const colA = items.slice(0, half);
  const colB = items.slice(half);

  return (
    <header data-testid="site-header" className="sticky top-0 z-50 w-full">
      {/* Top utility bar */}
      <div className="hidden lg:block bg-brand-navy-deep text-white/90 text-xs">
        <div className="container-x flex items-center justify-between h-9">
          <div className="flex items-center gap-5">
            <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {HOSPITAL.address.line1}, {HOSPITAL.address.city} – {HOSPITAL.address.pincode}</span>
            <a href={`tel:${HOSPITAL.phoneRaw}`} className="flex items-center gap-1.5 hover:text-white">
              <Phone className="w-3.5 h-3.5" /> 24×7 Emergency: {HOSPITAL.phone}
            </a>
          </div>
          <div className="flex items-center gap-4">
            <span>www.{HOSPITAL.domain}</span>
          </div>
        </div>
      </div>

      {/* Mobile mini emergency bar */}
      <a
        href={`tel:${HOSPITAL.phoneRaw}`}
        className="lg:hidden flex items-center justify-center gap-2 bg-brand-crimson text-white text-[11px] font-semibold tracking-wider uppercase py-1.5"
      >
        <Phone className="w-3.5 h-3.5 animate-heart-pulse" /> 24×7 Emergency · Call {HOSPITAL.phone}
      </a>

      <div className={`relative transition-all duration-300 ${scrolled ? "bg-white/95 backdrop-blur shadow-[0_2px_18px_-12px_rgba(19,60,121,0.4)]" : "bg-white"}`}>
        <div className="container-x flex items-center justify-between h-[64px] sm:h-[72px]">
          <Link to="/" data-testid="header-logo" className="flex items-center gap-2.5">
            <img src="/assets/logo.png" alt="Godhra Heart Hospital" className="h-10 sm:h-12 w-auto object-contain" />
            <div className="hidden sm:block leading-tight">
              <div className="font-display text-[15px] md:text-[17px] text-brand-navy-deep font-bold whitespace-nowrap">Godhra Heart Hospital</div>
              <div className="text-[10px] md:text-[10.5px] tracking-[0.18em] uppercase text-brand-crimson font-semibold">Super Specialty Care</div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-5 lg:gap-8">
            {NAV.map((n) => {
              if (n.mega) {
                const isOpen = activeMega === n.mega;
                return (
                  <div
                    key={n.to}
                    className="relative"
                    onMouseEnter={() => showMega(n.mega)}
                    onMouseLeave={hideMega}
                  >
                    <NavLink
                      to={n.to}
                      data-testid={`nav-${n.label.toLowerCase()}`}
                      end={n.to === "/"}
                      className={({ isActive }) => `nav-link inline-flex items-center gap-1 text-[13px] lg:text-sm font-semibold tracking-wide transition-colors whitespace-nowrap ${isActive ? "active text-brand-navy-deep" : "text-slate-700 hover:text-brand-navy-deep"}`}
                    >
                      {n.label}
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${isOpen ? "rotate-180 text-brand-crimson" : ""}`} />
                    </NavLink>
                  </div>
                );
              }
              return (
                <NavLink
                  key={n.to}
                  to={n.to}
                  data-testid={`nav-${n.label.toLowerCase()}`}
                  end={n.to === "/"}
                  className={({ isActive }) => `nav-link text-[13px] lg:text-sm font-semibold tracking-wide transition-colors whitespace-nowrap ${isActive ? "active text-brand-navy-deep" : "text-slate-700 hover:text-brand-navy-deep"}`}
                >
                  {n.label}
                </NavLink>
              );
            })}
          </nav>

          <div className="flex items-center gap-3">
            <Link to="/contact" data-testid="cta-book-appointment" className="btn-primary text-sm !hidden lg:!inline-flex">
              Book Appointment
            </Link>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              className="md:hidden p-2 rounded-md text-brand-navy-deep hover:bg-brand-sand"
              aria-label="Toggle menu"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mega menu panel (shared for services & doctors) */}
        <div
          data-testid={`mega-${activeMega || "none"}`}
          onMouseEnter={() => activeMega && showMega(activeMega)}
          onMouseLeave={hideMega}
          className={`hidden md:block absolute left-0 right-0 top-full origin-top transition-all duration-300 ease-out ${
            activeMega ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 -translate-y-2 pointer-events-none"
          }`}
        >
          {cfg && (
            <div className="container-x">
              <div className="bg-white border border-border rounded-b-2xl shadow-[0_24px_60px_-30px_rgba(19,60,121,0.35)] overflow-hidden">
                <div className="grid lg:grid-cols-12">
                  {/* Left intro */}
                  <div className="lg:col-span-4 bg-brand-navy-deep text-white p-7 relative overflow-hidden">
                    <div className="absolute inset-0 grain opacity-30" />
                    <div className="relative">
                      <div className="text-[10.5px] tracking-[0.25em] uppercase text-brand-crimson font-bold">{cfg.eyebrow}</div>
                      <h3 className="font-display text-2xl mt-2 leading-tight">{cfg.title}</h3>
                      <p className="text-white/75 text-sm mt-3 leading-relaxed">{cfg.desc}</p>
                      <Link to={cfg.ctaTo} className="btn-primary mt-5 text-xs">{cfg.ctaLabel}</Link>
                    </div>
                  </div>

                  {/* Right list */}
                  <div className={`lg:col-span-8 grid ${items.length > 4 ? "sm:grid-cols-2" : "grid-cols-1"} p-4`}>
                    {(items.length > 4 ? [colA, colB] : [items]).map((col, idx) => (
                      <ul key={idx} className="flex flex-col">
                        {col.map((it, i) => (
                          <li key={it.id}>
                            <Link
                              to={it.to}
                              data-testid={`mega-${activeMega}-${it.id}`}
                              className="group relative flex items-center justify-between gap-3 px-4 py-3 transition-colors duration-300 reveal"
                              style={{ animationDelay: `${i * 40}ms` }}
                            >
                              {/* animated top + bottom lines */}
                              <span className="pointer-events-none absolute left-4 right-4 top-0 h-px bg-brand-crimson scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500 ease-out" />
                              <span className="pointer-events-none absolute left-4 right-4 bottom-0 h-px bg-brand-crimson scale-x-0 group-hover:scale-x-100 origin-right transition-transform duration-500 ease-out" />
                              <span className="flex flex-col">
                                <span className="text-[13.5px] font-semibold text-slate-800 uppercase tracking-wide group-hover:text-brand-crimson transition-colors duration-300">
                                  {it.name}
                                </span>
                                {it.sub && (
                                  <span className="text-[11px] text-slate-500 mt-0.5 normal-case tracking-normal group-hover:text-brand-navy-deep transition-colors duration-300">{it.sub}</span>
                                )}
                              </span>
                              <ChevronDown className="w-4 h-4 -rotate-90 text-slate-400 group-hover:text-brand-crimson transition-all duration-300 group-hover:translate-x-1 shrink-0" />
                            </Link>
                          </li>
                        ))}
                      </ul>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Mobile menu */}
        {open && (
          <div data-testid="mobile-menu" className="md:hidden bg-white border-t border-border">
            <div className="container-x py-4 flex flex-col gap-1">
              {NAV.map((n) =>
                n.mega ? (
                  <div key={n.to}>
                    <button
                      data-testid={`mobile-${n.mega}-toggle`}
                      onClick={() => setMobileExpanded((v) => (v === n.mega ? null : n.mega))}
                      className="w-full flex items-center justify-between text-base font-semibold py-2 text-slate-700"
                    >
                      <span>{n.label}</span>
                      <ChevronDown className={`w-4 h-4 transition-transform ${mobileExpanded === n.mega ? "rotate-180 text-brand-crimson" : ""}`} />
                    </button>
                    <div className={`grid transition-all duration-300 ${mobileExpanded === n.mega ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
                      <ul className="overflow-hidden pl-3 border-l-2 border-brand-crimson/40 ml-1">
                        {MEGA_CONFIG[n.mega].items.map((it) => (
                          <li key={it.id}>
                            <Link to={it.to} className="block py-2 text-[13px] font-semibold text-slate-700 hover:text-brand-crimson uppercase tracking-wide">
                              {it.name}
                              {it.sub && <span className="block text-[11px] text-slate-500 normal-case tracking-normal">{it.sub}</span>}
                            </Link>
                          </li>
                        ))}
                        <li>
                          <Link to={MEGA_CONFIG[n.mega].ctaTo} className="block py-2 text-[13px] font-bold text-brand-navy-deep">{MEGA_CONFIG[n.mega].ctaLabel} →</Link>
                        </li>
                      </ul>
                    </div>
                  </div>
                ) : (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    end={n.to === "/"}
                    className={({ isActive }) => `text-base font-semibold py-2 ${isActive ? "text-brand-crimson" : "text-slate-700"}`}
                  >
                    {n.label}
                  </NavLink>
                )
              )}
              <Link to="/contact" className="btn-primary mt-2 self-start">Book Appointment</Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
